import React from 'react'

import { useState } from 'react'
import { isMobile } from 'react-device-detect'
import {
  MessageCircle,
  Paperclip,
  Download,
  X,
  ImageIcon,
  FileText,
  Video,
  Upload,
} from 'lucide-react'
import { DcButton } from 'pixel-base/components/dc-button/dc-button'
import styled from 'styled-components'

interface Attachment {
  id: string
  name: string
  size: number
  type: string
  url: string
  thumbnail?: string
}

interface Comment {
  id: string
  author: {
    name: string
    username: string
    avatar?: string
    role?: string
  }
  content: string
  timestamp: string
  likes: number
  isLiked: boolean
  attachments?: Attachment[]
  replies?: Comment[]
}

interface TeamCommentsProps {
  comments: Comment[]
  onAddComment?: (content: string, attachments?: File[]) => void
  showAddComment?: boolean
  currentUser?: {
    name: string
    avatar?: string
  }
}

const CardContent = styled.div`
  padding: 24px;
`

const Avatar = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
  background: #f3f4f6;
  display: flex;
  align-items: center;
  justify-content: center;
`

const AvatarImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`

const AvatarFallback = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
`

const Button = styled.button<{
  variant?: 'ghost' | 'primary'
  size?: 'sm' | 'md'
  $isLiked?: boolean
}>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  border-radius: 6px;
  font-size: ${(props) => (props.size === 'sm' ? '14px' : '16px')};
  font-weight: 500;
  transition: all 0.2s;
  cursor: pointer;
  border: none;

  ${(props) =>
    props.variant === 'ghost'
      ? `
    background: transparent;
    color: ${props.$isLiked ? '#ef4444' : '#6b7280'};
    padding: ${props.size === 'sm' ? '4px 8px' : '8px 16px'};
    height: ${props.size === 'sm' ? '32px' : '40px'};
    
    &:hover {
      background: #f3f4f6;
    }
    
    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  `
      : `
    background: #3b82f6;
    color: white;
    padding: ${props.size === 'sm' ? '6px 12px' : '8px 16px'};
    height: ${props.size === 'sm' ? '32px' : '40px'};
    
    &:hover {
      background: #2563eb;
    }
    
    &:disabled {
      background: #9ca3af;
      cursor: not-allowed;
    }
  `}
`

const Textarea = styled.textarea`
  width: 100%;
  min-height: 80px;
  padding: 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  line-height: 1.5;
  resize: none;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  &::placeholder {
    color: #9ca3af;
  }
`

const CommentContainer = styled.div<{ $isReply?: boolean }>`
  display: flex;
  gap: 12px;
  margin-left: ${(props) => (props.$isReply ? '48px' : '0')};
  margin-top: ${(props) => (props.$isReply ? '12px' : '0')};
`

const CommentContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const CommentHeader = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  font-size: 14px;
`

const AuthorName = styled.span`
  font-weight: 600;
  color: #111827;
`

const Username = styled.span`
  color: #6b7280;
`

const RoleBadge = styled.span`
  padding: 2px 8px;
  background: #dbeafe;
  color: #1e40af;
  font-size: 12px;
  border-radius: 12px;
`

const Timestamp = styled.span`
  color: #6b7280;
`

const CommentText = styled.p`
  font-size: 14px;
  line-height: 1.6;
  color: #374151;
  margin: 0;
`

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: between;
  margin-bottom: 24px;
`

const Title = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #111827;
  margin: 0;
  flex: 1;
`

const CommentsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`

const AddCommentContainer = styled.div`
  display: flex;
  gap: 12px;
`

const AddCommentContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const AddCommentActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`

const EmptyState = styled.div`
  text-align: center;
  padding: 32px 0;
  color: #6b7280;
`

const EmptyStateIcon = styled.div`
  margin: 0 auto 12px;
  opacity: 0.5;
`

const AttachmentContainer = styled.div`
  margin-top: 12px;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`

const AttachmentItem = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  font-size: 14px;
  max-width: 300px;
`

const AttachmentIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 4px;
  background: #e5e7eb;
  color: #6b7280;
`

const AttachmentInfo = styled.div`
  flex: 1;
  min-width: 0;
`

const AttachmentName = styled.div`
  font-weight: 500;
  color: #374151;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const AttachmentSize = styled.div`
  font-size: 12px;
  color: #6b7280;
`

const AttachmentActions = styled.div`
  display: flex;
  gap: 4px;
`

const AttachmentButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border: none;
  background: transparent;
  color: #6b7280;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #e5e7eb;
    color: #374151;
  }
`

const FileInput = styled.input`
  display: none;
`

const SliderOverlay = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  right: 0;
  width: 400px;
  height: 100vh;
  background: white;
  box-shadow: -2px 0 8px rgba(0, 0, 0, 0.1);
  transform: translateX(${(props) => (props.$isOpen ? '0' : '100%')});
  transition: transform 0.3s ease-in-out;
  z-index: 1000;
  display: flex;
  flex-direction: column;
`

const SliderHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px;
  border-bottom: 1px solid #e5e7eb;
`

const SliderTitle = styled.h3`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #111827;
`

const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  color: #6b7280;

  &:hover {
    background: #f3f4f6;
  }
`

const SliderContent = styled.div`
  flex: 1;
  padding: 20px;
  overflow-y: auto;
`

const DropZone = styled.div<{ $isDragOver: boolean }>`
  border: 2px dashed ${(props) => (props.$isDragOver ? '#3b82f6' : '#d1d5db')};
  border-radius: 8px;
  padding: 40px 20px;
  text-align: center;
  background: ${(props) => (props.$isDragOver ? '#eff6ff' : '#f9fafb')};
  transition: all 0.2s;
  cursor: pointer;
  margin-bottom: 20px;

  &:hover {
    border-color: #3b82f6;
    background: #eff6ff;
  }
`

const DropZoneIcon = styled.div`
  margin-bottom: 12px;
  color: #6b7280;
`

const DropZoneText = styled.div`
  font-size: 16px;
  font-weight: 500;
  color: #374151;
  margin-bottom: 4px;
`

const DropZoneSubtext = styled.div`
  font-size: 14px;
  color: #6b7280;
`

const SliderActions = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  padding: 20px;
  border-top: 1px solid #e5e7eb;
`

const Backdrop = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.5);
  opacity: ${(props) => (props.$isOpen ? 1 : 0)};
  visibility: ${(props) => (props.$isOpen ? 'visible' : 'hidden')};
  transition: all 0.3s ease-in-out;
  z-index: 999;
`

const ImagePreview = styled.img`
  max-width: 300px;
  max-height: 200px;
  border-radius: 8px;
  object-fit: cover;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  cursor: pointer;
  transition: transform 0.2s ease;

  &:hover {
    transform: scale(1.02);
  }
`

const VideoPreview = styled.video`
  max-width: 200px;
  max-height: 150px;
  border-radius: 6px;
`

function AttachmentDisplay({ attachments }: { attachments: Attachment[] }) {
  const getFileIcon = (type: string) => {
    if (type.startsWith('image/')) return <ImageIcon size={16} />
    if (type.startsWith('video/')) return <Video size={16} />
    return <FileText size={16} />
  }

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return (
      Number.parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
    )
  }

  const isImage = (type: string) => type.startsWith('image/')
  const isVideo = (type: string) => type.startsWith('video/')

  return (
    <AttachmentContainer>
      {attachments.map((attachment) => (
        <div key={attachment?.id}>
          {isImage(attachment?.type) ? (
            <div style={{ position: 'relative', display: 'inline-block' }}>
              <ImagePreview
                src={attachment?.url || '/placeholder.svg'}
                alt={attachment?.name}
                onClick={() => window.open(attachment?.url, '_blank')}
                title="Click to view full size"
              />
              <AttachmentButton
                onClick={() => window.open(attachment?.url, '_blank')}
                style={{
                  position: 'absolute',
                  top: '8px',
                  right: '8px',
                  background: 'rgba(0, 0, 0, 0.7)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  padding: '4px',
                  cursor: 'pointer',
                }}
                title="Download image"
              >
                <Download size={14} />
              </AttachmentButton>
            </div>
          ) : isVideo(attachment.type) ? (
            <VideoPreview controls>
              <source src={attachment?.url} type={attachment?.type} />
            </VideoPreview>
          ) : (
            <AttachmentItem>
              <AttachmentIcon>{getFileIcon(attachment?.type)}</AttachmentIcon>
              <AttachmentInfo>
                <AttachmentName>{attachment?.name}</AttachmentName>
                <AttachmentSize>
                  {formatFileSize(attachment?.size)}
                </AttachmentSize>
              </AttachmentInfo>
              <AttachmentActions>
                <AttachmentButton
                  onClick={() => window.open(attachment?.url, '_blank')}
                >
                  <Download size={14} />
                </AttachmentButton>
              </AttachmentActions>
            </AttachmentItem>
          )}
        </div>
      ))}
    </AttachmentContainer>
  )
}

function CommentItem({
  comment,
  isReply = false,
}: {
  comment: Comment
  isReply?: boolean
}) {
  const getInitials = (name: string) => {
    if (!name) return 'UN'
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2)
  }

  return (
    <div>
      <CommentContainer $isReply={isReply}>
        <Avatar>
          {comment.author.avatar ? (
            <AvatarImage
              src={comment.author.avatar || '/placeholder.svg'}
              alt={comment.author.name}
            />
          ) : (
            <AvatarFallback>{getInitials(comment.author.name)}</AvatarFallback>
          )}
        </Avatar>

        <CommentContent>
          <CommentHeader>
            <AuthorName>{comment.author.name}</AuthorName>
            {!isMobile && <Username>@{comment.author.username}</Username>}
            {comment.author.role && (
              <RoleBadge>{comment.author.role}</RoleBadge>
            )}
            {!isMobile && <span>•</span>}
          </CommentHeader>
          <Timestamp>{comment.timestamp}</Timestamp>

          {comment.content && <CommentText>{comment.content}</CommentText>}
          {comment.attachments && comment.attachments.length > 0 && (
            <>
              <div
                style={{
                  fontSize: '12px',
                  color: '#6b7280',
                  marginBottom: '8px',
                  fontWeight: '500',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                }}
              >
                <Paperclip size={12} />
                Attachments ({comment.attachments.length})
              </div>
              <AttachmentDisplay attachments={comment.attachments} />
            </>
          )}
        </CommentContent>
      </CommentContainer>

      {comment.replies && comment.replies.length > 0 && (
        <div>
          {comment.replies.map((reply) => (
            <CommentItem key={reply.id} comment={reply} isReply={true} />
          ))}
        </div>
      )}
    </div>
  )
}

export default function TeamComments({
  comments,
  onAddComment,
  showAddComment = true,
  currentUser,
}: TeamCommentsProps) {
  const [newComment, setNewComment] = useState('')
  const [attachments, setAttachments] = useState<File[]>([])
  const [isSliderOpen, setIsSliderOpen] = useState(false)
  const [sliderAttachments, setSliderAttachments] = useState<File[]>([])
  const [sliderNotes, setSliderNotes] = useState('')
  const [isDragOver, setIsDragOver] = useState(false)

  const handleAddComment = () => {
    if ((newComment.trim() || attachments.length > 0) && onAddComment) {
      onAddComment(newComment, attachments)
      setNewComment('')
      setAttachments([])
    }
  }

  const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleAddComment()
    }
  }

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || [])
    setAttachments((prev) => [...prev, ...files])
  }

  const removeAttachment = (index: number) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index))
  }

  const handleSliderFileSelect = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const files = Array.from(event.target.files || [])
    setSliderAttachments((prev) => [...prev, ...files])
  }

  const removeSliderAttachment = (index: number) => {
    setSliderAttachments((prev) => prev.filter((_, i) => i !== index))
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(true)
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(false)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(false)
    const files = Array.from(e.dataTransfer.files)
    setSliderAttachments((prev) => [...prev, ...files])
  }

  const handleSliderSubmit = () => {
    if (sliderAttachments.length > 0) {
      // Add the attachments to the main comment area
      setAttachments((prev) => [...prev, ...sliderAttachments])
      // Close slider and reset
      setIsSliderOpen(false)
      setSliderAttachments([])
      setSliderNotes('')
    }
  }

  const handleSliderClose = () => {
    setIsSliderOpen(false)
    setSliderAttachments([])
    setSliderNotes('')
  }

  const getInitials = (name: string) => {
    if (!name) return 'UN'
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2)
  }

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return (
      Number.parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
    )
  }

  return (
    <>
      <CardContent>
        <CommentsSection>
          <Header>
            <Title>Comments ({comments.length})</Title>
            <MessageCircle size={20} color="#6b7280" />
          </Header>

          {showAddComment && (
            <AddCommentContainer>
              <Avatar>
                {currentUser?.avatar ? (
                  <AvatarImage
                    src={currentUser.avatar || '/placeholder.svg'}
                    alt={currentUser.name}
                  />
                ) : (
                  <AvatarFallback>
                    {currentUser?.name ? getInitials(currentUser.name) : 'YU'}
                  </AvatarFallback>
                )}
              </Avatar>
              <AddCommentContent>
                <Textarea
                  placeholder={
                    attachments.length > 0
                      ? 'Add a comment (optional)...'
                      : 'Add a comment...'
                  }
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  onKeyPress={handleKeyPress}
                  style={{ minHeight: '100px' }}
                />
                {attachments.length > 0 && (
                  <>
                    <div
                      style={{
                        fontSize: '12px',
                        color: '#6b7280',
                        marginBottom: '8px',
                        fontWeight: '500',
                      }}
                    >
                      Attachments ({attachments.length})
                    </div>
                    <AttachmentContainer>
                      {attachments.map((file, index) => (
                        <AttachmentItem key={index}>
                          <AttachmentIcon>
                            {file.type.startsWith('image/') ? (
                              <ImageIcon size={16} />
                            ) : (
                              <FileText size={16} />
                            )}
                          </AttachmentIcon>
                          <AttachmentInfo>
                            <AttachmentName>{file.name}</AttachmentName>
                            <AttachmentSize>
                              {formatFileSize(file.size)}
                            </AttachmentSize>
                          </AttachmentInfo>
                          <AttachmentActions>
                            <AttachmentButton
                              onClick={() => removeAttachment(index)}
                            >
                              <X size={14} />
                            </AttachmentButton>
                          </AttachmentActions>
                        </AttachmentItem>
                      ))}
                    </AttachmentContainer>
                  </>
                )}
                <AddCommentActions>
                  <DcButton
                    variant="outline-info"
                    size="sm"
                    type="button"
                    onClick={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      setIsSliderOpen(true)
                    }}
                    withIcon={true}
                  >
                    <Paperclip size={16} />
                    <span>Attach</span>
                  </DcButton>
                  <DcButton
                    variant="primary"
                    size="sm"
                    type="button"
                    onClick={handleAddComment}
                    disabled={!newComment.trim() && attachments.length === 0}
                  >
                    {attachments.length > 0 ? 'Post Comment' : 'Comment'}
                  </DcButton>
                </AddCommentActions>
              </AddCommentContent>
            </AddCommentContainer>
          )}

          <div style={{ maxHeight: '230px', overflowY: 'auto' }}>
            {comments.map((comment) => (
              <CommentItem key={comment.id} comment={comment} />
            ))}
          </div>

          {comments.length === 0 && (
            <EmptyState>
              <EmptyStateIcon>
                <MessageCircle size={48} />
              </EmptyStateIcon>
              <p>No comments yet. Be the first to share your thoughts!</p>
            </EmptyState>
          )}
        </CommentsSection>
      </CardContent>

      {/* Attachment Slider */}
      <Backdrop $isOpen={isSliderOpen} onClick={handleSliderClose} />
      <SliderOverlay $isOpen={isSliderOpen}>
        <SliderHeader>
          <SliderTitle>Select Files to Attach</SliderTitle>
          <CloseButton onClick={handleSliderClose}>
            <X size={20} />
          </CloseButton>
        </SliderHeader>

        <SliderContent>
          <DropZone
            $isDragOver={isDragOver}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() =>
              document.getElementById('slider-file-input')?.click()
            }
          >
            <DropZoneIcon>
              <Upload size={32} />
            </DropZoneIcon>
            <DropZoneText>
              {isDragOver ? 'Drop files here' : 'Drag & drop files to attach'}
            </DropZoneText>
            <DropZoneSubtext>or click to browse files</DropZoneSubtext>
            <FileInput
              id="slider-file-input"
              type="file"
              multiple
              accept="image/*,video/*,.pdf,.doc,.docx,.txt"
              onChange={handleSliderFileSelect}
            />
          </DropZone>

          {sliderAttachments.length > 0 && (
            <AttachmentContainer>
              {sliderAttachments.map((file, index) => (
                <AttachmentItem key={index}>
                  <AttachmentIcon>
                    {file.type.startsWith('image/') ? (
                      <ImageIcon size={16} />
                    ) : (
                      <FileText size={16} />
                    )}
                  </AttachmentIcon>
                  <AttachmentInfo>
                    <AttachmentName>{file.name}</AttachmentName>
                    <AttachmentSize>{formatFileSize(file.size)}</AttachmentSize>
                  </AttachmentInfo>
                  <AttachmentActions>
                    <AttachmentButton
                      onClick={() => removeSliderAttachment(index)}
                    >
                      <X size={14} />
                    </AttachmentButton>
                  </AttachmentActions>
                </AttachmentItem>
              ))}
            </AttachmentContainer>
          )}
        </SliderContent>

        <SliderActions>
          <Button variant="ghost" size="sm" onClick={handleSliderClose}>
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={handleSliderSubmit}
            disabled={sliderAttachments.length === 0}
          >
            Add Attachments
          </Button>
        </SliderActions>
      </SliderOverlay>
    </>
  )
}
